import { useEffect } from 'react';
import { exchangeAccessSession } from '../accessSession';

/**
 * Keep the HttpOnly session cookie in sync with Firebase's rotating ID token
 * (GCP auth path only).
 *
 * Firebase ID tokens expire after ~1 hour and the SDK auto-refreshes them a
 * few minutes before expiry. Without re-posting the rotated token the session
 * cookie goes stale and the next API call 401s — silently logging the user out
 * mid-session. This subscribes to `onIdTokenChanged` and re-posts each fresh
 * token to /auth/gcp/session so the cookie tracks the token for the whole
 * session.
 *
 * Firebase is lazy-imported so it stays out of the initial bundle.
 */
export function useGcpSessionSync(): void {
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    const lifecycle = new AbortController();

    void (async () => {
      const { subscribeIdTokenRefresh } = await import('../gcp/gcpAuth');
      if (lifecycle.signal.aborted) return;
      // Firebase invokes this observer IMMEDIATELY with the current auth
      // state, not only when the token rotates. That first call carries the
      // token we already exchanged to get here, so re-posting it is at best a
      // wasted round-trip on every page load — and it used to cost the user a
      // second MFA code email, because the backend still saw a sign-in inside
      // its freshness window and issued a new one. Only rotations are worth
      // posting, so skip the initial fire.
      let seenInitial = false;
      unsubscribe = subscribeIdTokenRefresh((idToken) => {
        // Sign-out (null token) is handled by AuthLayout's profile check.
        if (!idToken) return;
        if (!seenInitial) {
          seenInitial = true;
          return;
        }
        // Fire-and-forget cookie refresh. A failure isn't fatal here: the next
        // protected request will 401 and the api interceptor routes to /login.
        void exchangeAccessSession(idToken).catch(() => undefined);
      });
    })();

    return () => {
      lifecycle.abort();
      unsubscribe?.();
    };
  }, []);
}
